import { getCookieInfo } from './getCookie.js';

/**
 * 获取请求头配置
 * @returns {Object} 请求头对象
 */
function getHeaders() {
    const token = localStorage.getItem('token') || getCookieInfo('access_token') || '';
    const loginType = localStorage.getItem('loginType') || '';
    
    const headers = {
        'Content-Type': 'application/json',
        'userfrom': 'tcmkb',
        'Accept': 'text/event-stream'
    };
    
    if (token) {
        headers['Authorization'] ='bearer' +token; 
    }
    
    if (loginType) {
        headers['X-Login-T'] = loginType;
    }
    
    return headers;
}

/**
 * 解析流式数据
 * @param {string} buffer - 数据缓冲区
 * @param {Function} onMessage - 消息回调函数
 * @returns {string} 剩余的不完整数据
 */
function parseStreamData(buffer, onMessage) {
    if (!buffer) return '';
    
    // 处理可能的多行数据（SSE 格式或 JSON 行格式）
    const lines = buffer.split('\n');
    const remainingBuffer = lines.pop() || ''; // 保留最后一个不完整的行
    
    lines.forEach(line => {
        const trimmedLine = line.trim();
        if (!trimmedLine) return;
        
        try {
            // 尝试解析 SSE 格式 (data: {...})
            if (trimmedLine.startsWith('data: ')) {
                const jsonStr = trimmedLine.substring(6).trim();
                if (jsonStr && jsonStr !== '[DONE]') {
                    const data = JSON.parse(jsonStr);
                    if (onMessage) {
                        onMessage(data);
                    }
                }
            } else if (trimmedLine.startsWith('{') || trimmedLine.startsWith('[')) {
                // 尝试直接解析 JSON（以 { 或 [ 开头）
                const data = JSON.parse(trimmedLine);
                if (onMessage) {
                    onMessage(data);
                }
            } else {
                // 纯文本流，直接传递
                if (onMessage) {
                    onMessage({ text: trimmedLine });
                }
            }
        } catch (e) {
            // 解析失败，作为纯文本处理
            if (onMessage) {
                onMessage({ text: trimmedLine });
            }
        }
    });
    
    return remainingBuffer;
}

/**
 * 流式请求封装函数
 * @param {string} url - 请求地址（完整路径或相对路径）
 * @param {Object} params - 请求参数
 * @param {Function} onMessage - 接收到消息时的回调函数
 * @param {Function} onError - 错误处理回调函数
 * @param {Function} onComplete - 完成时的回调函数
 * @param {Object} options - 额外配置选项
 * @param {string} options.baseURL - 基础URL，默认为 '/tcmkb-api'
 * @param {string} options.method - 请求方法，默认为 'POST'
 * @returns {Promise} Promise 对象
 */
export function streamFetch(url, params = {}, onMessage, onError, onComplete, options = {}) {
    const {
        baseURL = '/tcmkb-api',
        method = 'POST'
    } = options;
    
    // 构建完整URL
    const fullUrl = `${baseURL}${url}`;
    
    // 获取请求头
    const headers = getHeaders();
    
    // 使用原生 fetch API 支持流式响应
    return window.fetch(fullUrl, {
        method: method,
        headers: headers,
        credentials: 'include',
        body: method.toUpperCase() === 'POST' ? JSON.stringify(params) : undefined
    })
    .then(response => {
        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';
        
        function readStream() {
            return reader.read().then(({ done, value }) => {
                if (done) {
                    // 处理剩余的缓冲区数据
                    if (buffer.trim() && onMessage) {
                        parseStreamData(buffer, onMessage);
                    }
                    if (onComplete) {
                        onComplete();
                    }
                    return;
                }
                
                // 解码数据块
                buffer += decoder.decode(value, { stream: true });
                
                // 解析流式数据
                buffer = parseStreamData(buffer, onMessage);
                
                return readStream();
            });
        }
        
        return readStream();
    })
    .catch(error => {
        if (onError) {
            onError(error);
        } else {
            throw error;
        }
    });
}

/**
 * 流式 POST 请求（便捷方法）
 * @param {string} url - 请求地址
 * @param {Object} params - 请求参数
 * @param {Function} onMessage - 消息回调
 * @param {Function} onError - 错误回调
 * @param {Function} onComplete - 完成回调
 * @param {Object} options - 额外配置
 * @returns {Promise} Promise 对象
 */
export function streamPost(url, params = {}, onMessage, onError, onComplete, options = {}) {
    return streamFetch(url, params, onMessage, onError, onComplete, {
        ...options,
        method: 'POST'
    });
}

/**
 * 流式 GET 请求（便捷方法）
 * @param {string} url - 请求地址
 * @param {Object} params - 请求参数（将作为查询参数）
 * @param {Function} onMessage - 消息回调
 * @param {Function} onError - 错误回调
 * @param {Function} onComplete - 完成回调
 * @param {Object} options - 额外配置
 * @returns {Promise} Promise 对象
 */
export function streamGet(url, params = {}, onMessage, onError, onComplete, options = {}) {
    // 将参数转换为查询字符串
    const queryString = new URLSearchParams(params).toString();
    const fullUrl = queryString ? `${url}?${queryString}` : url;
    
    return streamFetch(fullUrl, {}, onMessage, onError, onComplete, {
        ...options,
        method: 'GET'
    });
}

